// ── Le resume du plan que lit le modele (chantier COP) ──
// Un texte court, en centimetres, pour l'etage actif : les pieces, les murs et les ouvertures. Les murs portent un
// identifiant court (m1, m2…), que `ids` retraduit au retour.
import { computeRoomModel, cmToPlanPx, planPx2ToM2 } from '@/lib/rooms/catalog';
import type { ProjectState, Wall } from '@/lib/types';

/** Au-dela, le resume ne liste que les premiers : il doit tenir dans le contexte du modele */
const MAX_WALLS = 200;
const MAX_OPENINGS = 200;

export const pxToCm = (px: number, cal: ProjectState['calibration']) => px / cmToPlanPx(1, cal);

/** Un element sans etage appartient au premier etage du projet */
export const onFloor = (p: ProjectState, etageId: string) => (e: { floorId?: string }) =>
  (e.floorId ?? p.floors[0]?.id) === etageId;

export interface ResumePlan {
  texte: string;
  /** m1 → l'identifiant reel du mur */
  ids: Record<string, string>;
}

export function resumerPlan(p: ProjectState, etageId: string): ResumePlan {
  const cal = p.calibration;
  const cm = (px: number) => Math.round(pxToCm(px, cal));
  const here = onFloor(p, etageId);
  const walls = p.walls.filter(here);
  const listed = walls.slice(0, MAX_WALLS);
  const ids: Record<string, string> = {};
  const short = new Map<string, string>();
  listed.forEach((w, i) => {
    ids[`m${i + 1}`] = w.id;
    short.set(w.id, `m${i + 1}`);
  });

  const wallLine = (w: Wall) => {
    const len = cm(Math.hypot(w.end.x - w.start.x, w.end.y - w.start.y));
    const type = w.wallType === 'partition' ? 'cloison' : 'extérieur';
    return `${short.get(w.id)} (${cm(w.start.x)},${cm(w.start.y)})→(${cm(w.end.x)},${cm(w.end.y)}) ${len} cm, ${type} ${w.thickness} cm${w.locked ? ', verrouillé' : ''}`;
  };

  const model = computeRoomModel(walls, (p.roomSeparators ?? []).filter(here), (p.spaces ?? []).filter(here), cal);
  const rooms = model.resolved.flatMap(({ space, face }) => {
    if (!face) return [`${space.name} (non fermée)`];
    const xs = face.outline.map(q => q.x);
    const ys = face.outline.map(q => q.y);
    const area = planPx2ToM2(face.area, cal).toFixed(1).replace('.', ',');
    return [`${space.name} (de ${cm(Math.min(...xs))},${cm(Math.min(...ys))} à ${cm(Math.max(...xs))},${cm(Math.max(...ys))}, ${area} m²)`];
  });

  const openings = p.openings.filter(here).filter(o => o.wallId && short.has(o.wallId)).slice(0, MAX_OPENINGS).map(o => {
    const name = o.type === 'door' ? 'porte' : 'fenêtre';
    return `${name} de ${o.width} cm sur ${short.get(o.wallId!)}, position ${o.position.toFixed(2)}`;
  });

  const texte = [
    'Unités : centimètres ; origine en haut à gauche ; x grandit vers la droite, y grandit vers le bas.',
    `Pièces :\n${rooms.join('\n') || 'aucune'}`,
    `Murs :\n${listed.map(wallLine).join('\n') || 'aucun'}${walls.length > listed.length ? `\n(et ${walls.length - listed.length} autres murs)` : ''}`,
    `Ouvertures :\n${openings.join('\n') || 'aucune'}`,
  ].join('\n');
  return { texte, ids };
}

/** Le mur de l'etage le plus proche d'un point en cm, et la position du point sur lui (C7) */
export function murLePlusProche(p: ProjectState, etageId: string, point: { x: number; y: number }): { mur: string; position: number } | null {
  const cal = p.calibration;
  let best: { mur: string; position: number; d: number } | null = null;
  p.walls.filter(onFloor(p, etageId)).slice(0, MAX_WALLS).forEach((w, i) => {
    const ax = pxToCm(w.start.x, cal);
    const ay = pxToCm(w.start.y, cal);
    const dx = pxToCm(w.end.x, cal) - ax;
    const dy = pxToCm(w.end.y, cal) - ay;
    const len2 = dx * dx + dy * dy;
    if (len2 === 0) return;
    const t = Math.max(0, Math.min(1, ((point.x - ax) * dx + (point.y - ay) * dy) / len2));
    const d = Math.hypot(ax + t * dx - point.x, ay + t * dy - point.y);
    if (!best || d < best.d) best = { mur: `m${i + 1}`, position: t, d };
  });
  const found = best as { mur: string; position: number; d: number } | null;
  return found ? { mur: found.mur, position: found.position } : null;
}
